import React, { useState } from 'react';

// ═══════════════════════════════════════
// DATA BANNER
// ═══════════════════════════════════════
/**
 * Shows data source status — mock demo / stale cache / live
 * type: 'demo' | 'stale' | 'live' | 'error'
 */
export function DataBanner({ type = 'demo', message, dismissible = true }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const configs = {
    demo:  { bg: '#FEF3C7', color: '#D97706', border: '#FCD34D', icon: '⚠', prefix: 'Demo mode:' },
    stale: { bg: '#FEF3C7', color: '#D97706', border: '#FCD34D', icon: '⏱', prefix: 'Cached data:' },
    error: { bg: '#FEE2E2', color: '#DC2626', border: '#FECACA', icon: '✗', prefix: 'Data error:' },
    live:  { bg: '#D1FAE5', color: '#059669', border: '#A7F3D0', icon: '✓', prefix: 'Live feed:' },
  };
  const c = configs[type] || configs.demo;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 9,
      padding: '9px 28px',
      fontSize: 12,
      fontWeight: 500,
      borderBottom: `1px solid ${c.border}`,
      background: c.bg,
      color: c.color,
    }}>
      <span style={{ fontSize: 13 }}>{c.icon}</span>
      <span>
        <strong>{c.prefix}</strong> {message}
      </span>
      {dismissible && (
        <button
          onClick={() => setDismissed(true)}
          style={{
            marginLeft: 'auto',
            cursor: 'pointer',
            opacity: 0.6,
            fontSize: 18,
            lineHeight: 1,
            background: 'none',
            border: 'none',
            color: 'inherit',
            padding: '0 2px',
          }}>
          ×
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// LOADING SKELETON
// ═══════════════════════════════════════
export function LoadingSkeleton({ rows = 8 }) {
  return (
    <div style={{ padding: '8px 0' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 20px',
          borderBottom: '1px solid #F1F5F9',
        }}>
          <div className="skeleton" style={{ width: 28, height: 28, borderRadius: 6 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div className="skeleton" style={{ width: `${120 + Math.random() * 60}px`, height: 13 }} />
            <div className="skeleton" style={{ width: `${60 + Math.random() * 40}px`, height: 10 }} />
          </div>
          {[1, 2, 3].map(j => (
            <div key={j} className="skeleton" style={{ width: 100, height: 30, borderRadius: 4 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════
// SPARKLINE
// ═══════════════════════════════════════
export function SparkLine({ data, up, width = 56, height = 22 }) {
  if (!data || data.length < 2) return <span style={{ color: '#94A3B8', fontSize: 11 }}>—</span>;

  const W = width, H = height;
  const min = Math.min(...data), max = Math.max(...data);
  const rng = max - min || 1;

  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - ((v - min) / rng) * (H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const color = up ? '#DC2626' : '#059669';

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ═══════════════════════════════════════
// COMING SOON
// ═══════════════════════════════════════
export function ComingSoon({ num, title, description, eta }) {
  return (
    <div>
      <div style={{
        background: '#fff',
        padding: '18px 28px 16px',
        borderBottom: '1px solid #E2E8F0',
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          color: '#00A896',
          marginBottom: 4,
          fontWeight: 500,
        }}>
          Module {num} / 05
        </div>
        <div style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 20,
          fontWeight: 600,
          color: '#0D1B2A',
          letterSpacing: '-0.4px',
        }}>
          {title}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        padding: 40,
      }}>
        <div style={{
          background: '#fff',
          border: '1px solid #E2E8F0',
          borderRadius: 14,
          padding: 48,
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
        }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: '#F6F8FA',
            border: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            margin: '0 auto 20px',
          }}>
            🔒
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: '#00A896',
            fontWeight: 500,
            letterSpacing: '0.5px',
            marginBottom: 10,
          }}>
            {eta}
          </div>
          <div style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 18,
            fontWeight: 600,
            color: '#0D1B2A',
            marginBottom: 12,
          }}>
            {title}
          </div>
          <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>
            {description}
          </div>
        </div>
      </div>
    </div>
  );
}
